import { useQuery } from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from '../envelope';
import { findSharedWorkspace } from './shared-workspaces';
import { iamKeys } from './types';

const foundationApi = getHttpClient('foundationApi');

export interface SharedProjectGrant {
  role_id: string;
  role_title: string;
  via: 'user' | 'team';
  team_name?: string;
  /** The parent project the role was granted on, when this project is below it. */
  from_project?: string;
}

/** An active project the caller holds a role in, in any organization (GET /iam/me/shared-projects). */
export interface SharedProject {
  project_id: string;
  name: string;
  parent_id?: string | null;
  org_id: string;
  org_name: string;
  customer_id: string;
  grants: SharedProjectGrant[];
}

/** "Platform: Compute Viewer through team Dat-G" — what a person was given on one project, in words. */
export function describeSharedProject(p: SharedProject): string {
  const how = p.grants
    .map((g) => {
      const through = g.via === 'team' ? `through team ${g.team_name || '(unnamed)'}` : 'given to you';
      return `${g.role_title} ${through}${g.from_project ? `, from ${g.from_project}` : ''}`;
    })
    .join('; ');
  return how ? `${p.name}: ${how}` : p.name;
}

/** Projects of other organizations (and your own) that a role reaches you on; their names are not readable otherwise. */
export function useSharedProjects() {
  return useQuery({
    queryKey: iamKeys.mySharedProjects,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<SharedProject[]> => {
      const res = await foundationApi.get<Envelope<{ projects?: SharedProject[] }>>('/iam/me/shared-projects');
      return res.data?.data?.projects ?? [];
    },
  });
}

/**
 * Whose machines a compute console opened through a share shows: the organization's name as the person knows it
 * from Teams (taken from a project of it they reach), else the compute workspace's own label.
 */
export function sharedOrgName(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string; name: string }[],
  projects: Pick<SharedProject, 'customer_id' | 'org_name'>[],
): string | undefined {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return undefined;
  return projects.find((p) => p.customer_id === w.organization_id)?.org_name || w.name || undefined;
}

/** The names of the projects of that organization the person reaches, for a banner: "Platform, Default" or "Platform and 2 more". */
export function sharedProjectNames(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string }[],
  projects: Pick<SharedProject, 'customer_id' | 'name'>[],
  max = 3,
): string {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return '';
  const names = projects.filter((p) => p.customer_id === w.organization_id).map((p) => p.name);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
}

/**
 * One entry of the top-bar project selector. `organizationId` is the organization the project belongs to ('' for an own
 * project while the home organization is unknown); `orgName` is set only when the project is in another organization.
 */
export interface SwitcherProject {
  id: string;
  name: string;
  organizationId: string;
  orgName?: string;
}

/** Own active projects, then the projects of other organizations the person reaches (marked by `orgName`). */
export function switcherProjects(
  own: { id: string; name: string }[],
  shared: Pick<SharedProject, 'project_id' | 'name' | 'org_name' | 'customer_id'>[],
  ownCustomerId: string | undefined,
): SwitcherProject[] {
  const seen = new Set(own.map((p) => p.id));
  const members = shared
    .filter((p) => !seen.has(p.project_id) && !!ownCustomerId && p.customer_id !== ownCustomerId)
    .map((p) => ({ id: p.project_id, name: p.name, organizationId: p.customer_id, orgName: p.org_name ?? '' }));
  return [...own.map((p) => ({ id: p.id, name: p.name, organizationId: ownCustomerId ?? '' })), ...members];
}

/**
 * The project a compute console opened through a share is about: the one the person chose if it belongs to that
 * organization, else the only one they reach there, else none.
 */
export function sharedConsoleProject(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string }[],
  projects: Pick<SharedProject, 'project_id' | 'customer_id'>[],
  chosen: string,
): string {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return '';
  const ids = projects.filter((p) => p.customer_id === w.organization_id).map((p) => p.project_id);
  if (chosen && ids.includes(chosen)) return chosen;
  return ids.length === 1 ? ids[0] : '';
}
